/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import * as AdaptiveCards from 'adaptivecards';

export type AdaptiveCard = {
  type: 'AdaptiveCard';
  body: readonly (TextBlock | Image | FactSet)[];
  actions?: readonly (ActionOpenUrl | ActionSubmit)[];
  $schema: string;
  version: string;
};

type TextBlock = {
  type: 'TextBlock';
  text: string;
  weight?: 'Lighter' | 'Default' | 'Bolder';
  size?: 'Small' | 'Default' | 'Medium' | 'Large' | 'ExtraLarge';
  wrap?: boolean;
};

type Image = {
  type: 'Image';
  url: string;
  size?: 'Small' | 'Medium' | 'Large' | 'Auto' | 'Stretch';
  altText?: string;
};

type FactSet = {
  type: 'FactSet';
  facts: readonly { title: string; value: string }[];
};

type ActionOpenUrl = {
  type: 'Action.OpenUrl';
  title: string;
  url: string;
};

type ActionSubmit = {
  type: 'Action.Submit';
  title: string;
};

type AdaptiveCardProps = {
  cardData: AdaptiveCard;
};

export const AdaptiveCardComponent: React.FC<AdaptiveCardProps> = ({ cardData }) => {
  const cardContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const adaptiveCard = new AdaptiveCards.AdaptiveCard();
    adaptiveCard.hostConfig = new AdaptiveCards.HostConfig({
      fontFamily: 'Poppins, sans-serif',
    });

    const addChoiceSetLabels = (items: any[]) => {
      if (!Array.isArray(items)) {
        return;
      }

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type === 'Input.ChoiceSet' && item.label) {
          const inlineElements = [
            { type: 'TextRun', text: item.label },
            { type: 'TextRun', text: ' *', color: 'Attention' },
          ];

          items.splice(i, 0, {
            type: 'RichTextBlock',
            inlines: inlineElements,
            spacing: 'Small',
          });

          delete item.label;
          i++;
        }

        if (item.items) {
          addChoiceSetLabels(item.items);
        } else if (item.columns) {
          item.columns.forEach((column: any) => addChoiceSetLabels(column.items));
        }
      }
    };

    const updatedCardData = JSON.parse(JSON.stringify(cardData));
    if (updatedCardData.body) {
      addChoiceSetLabels(updatedCardData.body);
    }

    adaptiveCard.parse(updatedCardData);

    if (cardContainerRef.current) {
      cardContainerRef.current.innerHTML = '';
      const renderedCard = adaptiveCard.render();
      if (renderedCard) {
        cardContainerRef.current.appendChild(renderedCard);
      }
    }
  }, [cardData]);

  return <div ref={cardContainerRef}></div>;
};
